import { For, Switch, Match, type Component } from "solid-js";
import "./skeleton.css";

export interface SkeletonProps {
  variant?: "line" | "block" | "table";
  rows?: number;
  width?: string;
  height?: string;
}

/**
 * Composant Skeleton réutilisable pour afficher des placeholders animés
 * lors du chargement de contenu.
 *
 * Variants :
 * - `line` : barre grise arrondie animée (défaut)
 * - `block` : bloc rectangulaire animé
 * - `table` : grille de `rows` lignes empilées
 *
 * Usage :
 * ```tsx
 * <Skeleton variant="line" width="60%" />
 * <Skeleton variant="block" width="100%" height="200px" />
 * <Skeleton variant="table" rows={5} />
 * ```
 */
export const Skeleton: Component<SkeletonProps> = (props) => {
  const variant = () => props.variant || "line";
  const rows = () => props.rows || 5;
  const width = () => props.width || "100%";
  const height = () => props.height || undefined;

  return (
    <Switch>
      <Match when={variant() === "line"}>
        <div
          class="skeleton skeleton--line"
          style={{
            width: width(),
            height: height() || "16px",
          }}
        />
      </Match>
      <Match when={variant() === "block"}>
        <div
          class="skeleton skeleton--block"
          style={{
            width: width(),
            height: height() || "200px",
          }}
        />
      </Match>
      <Match when={variant() === "table"}>
        <div class="skeleton skeleton--table">
          <For each={Array.from({ length: rows() })}>
            {() => (
              <div class="skeleton__row">
                <div class="skeleton__cell skeleton--line" style={{ flex: "1" }} />
                <div class="skeleton__cell skeleton--line" style={{ flex: "1" }} />
                <div class="skeleton__cell skeleton--line" style={{ flex: "0.8" }} />
              </div>
            )}
          </For>
        </div>
      </Match>
    </Switch>
  );
};
